import {
  DriverPayAllocationReplacement,
  Maybe,
  Scalars,
  useReplaceAllocationMutation,
} from "@api/graphql/generated/generated-types";
import { ApolloError } from "@apollo/client";
import React, { useEffect, useRef, useState } from "react";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import DriverSelect from "../DriverSelect/DriverSelect";
import { useCourieStore } from "@src/common/lib/store";
import { getUserFacingErrorMessage } from "@src/common/lib/NetworkErrorHandling";
import CourieButton from "../Button/Button";
import PercentInput from "../Input/PercentInput";
import { useAuth } from "@src/common/hooks/useAuth";
import DollarInput from "../Input/DollarInput";
import { SIZE } from "baseui/select";
import { Select } from "@src/common/components";
import { useDriverOptions } from "@src/common/hooks/useDriverOptions";

export type DriverPayAllocationInput = {
  allocationRate?: Maybe<Scalars["Money"]>;
  allocationAmount?: Maybe<Scalars["Money"]>;
  driverId: Scalars["ID"];
};

type Props = {
  driverPayAllocations: DriverPayAllocationInput[] | undefined;
  shipmentId: string | undefined;
  onConfirm?: () => void;
};

function ReplaceAllocationView({
  driverPayAllocations,
  shipmentId,
  onConfirm,
}: Props) {
  const { courierId } = useAuth();
  const { showToast } = useCourieStore();
  const [errorMessage, setErrorMessage] = useState("");
  const [allocations, setAllocations] = useState<DriverPayAllocationInput[]>(
    [],
  );
  // True once the user edits the rows locally; blocks server re-syncs until saved
  const hasUnsavedEditsRef = useRef(false);
  // Bumped on every local edit so a save can tell whether rows changed mid-flight
  const editVersionRef = useRef(0);
  const syncedShipmentIdRef = useRef<string | undefined>(undefined);
  const [replaceAllocationMutation, { loading }] =
    useReplaceAllocationMutation();
  const { driverOptions, loading: getDriversLoading } = useDriverOptions({
    courierId: courierId || undefined,
  });

  useEffect(() => {
    if (driverPayAllocations) {
      if (
        ["SELECT"].includes((document.activeElement as HTMLElement).tagName)
      ) {
        return;
      }
      // The shipments list polls, handing us a new driverPayAllocations
      // reference every few seconds, so never overwrite unsaved edits for the
      // same shipment. Selecting another shipment always resets the rows.
      if (
        hasUnsavedEditsRef.current &&
        syncedShipmentIdRef.current === shipmentId
      ) {
        return;
      }
      syncedShipmentIdRef.current = shipmentId;
      hasUnsavedEditsRef.current = false;
      setAllocations(driverPayAllocations);
    }
  }, [driverPayAllocations, shipmentId]);

  const updateAllocations = (updatedAllocations: DriverPayAllocationInput[]) => {
    hasUnsavedEditsRef.current = true;
    editVersionRef.current += 1;
    setAllocations(updatedAllocations);
  };

  const handleDriverChange = (index: number, driverId: Scalars["ID"]) => {
    const updatedAllocations = [...allocations];
    updatedAllocations[index].driverId = driverId;
    updateAllocations(updatedAllocations);
  };

  const handleAmountChange = (index: number, amount: number) => {
    const updatedAllocations = [...allocations];
    updatedAllocations[index].allocationAmount = amount;
    updateAllocations(updatedAllocations);
    setErrorMessage("");
  };

  const handleRateChange = (index: number, rate: number) => {
    const updatedAllocations = [...allocations];
    const totalRate = updatedAllocations.reduce(
      (acc, alloc, i) => (i === index ? acc : acc + alloc.allocationRate),
      0,
    );
    const rateDecimal = rate / 100;
    if (rate < 0 || rate > 100) {
      setErrorMessage("Allocation rate must be between 0 and 100%");
      return;
    }
    if (totalRate + rateDecimal > 1) {
      setErrorMessage("Total allocation rate cannot exceed 100%");
      return;
    }
    updatedAllocations[index].allocationRate = rateDecimal;
    updateAllocations(updatedAllocations);
    setErrorMessage("");
  };

  const handleRemoveAllocation = (index: number) => {
    const updatedAllocations = allocations.filter((_, i) => i !== index);
    updateAllocations(updatedAllocations);
  };

  const handleSave = () => {
    if (!shipmentId || !courierId) return;
    if (allocations.find((x) => x.driverId === "")) {
      setErrorMessage("All allocations must be assigned to a driver");
      return;
    }
    if (allocations.find((x) => x.allocationRate !== null)) {
      const totalRate = allocations.reduce(
        (acc, alloc) => acc + parseFloat(alloc.allocationRate),
        0,
      );
      if (Math.abs(totalRate - 1) > 0.000001) {
        setErrorMessage("Total allocation rate must be exactly 100%");
        return;
      }
    }
    const driverPayAllocationInputs = allocations.map(
      ({ driverId, allocationRate, allocationAmount }) => ({
        driverId,
        allocationRate,
        allocationAmount,
      }),
    );
    const replacement: DriverPayAllocationReplacement = {
      allocations: driverPayAllocationInputs,
      shipmentId,
    };
    const submittedEditVersion = editVersionRef.current;
    replaceAllocationMutation({
      variables: {
        courierId: courierId,
        replacement,
      },
    })
      .then(() => {
        // Rows edited while the save was in flight were not submitted; keep
        // them protected from the poll until they are saved too.
        if (editVersionRef.current === submittedEditVersion) {
          hasUnsavedEditsRef.current = false;
        }
        showToast({
          message: "Allocations updated successfully",
          type: "success",
        });
        onConfirm && onConfirm();
        errorMessage && setErrorMessage("");
      })
      .catch((error: ApolloError) => {
        const errorMessage =
          getUserFacingErrorMessage(error) ?? "Failed to update allocations";
        showToast({ message: errorMessage, type: "error" });
        setErrorMessage(errorMessage);
      });
  };

  const handleAddAllocation = () => {
    updateAllocations([
      ...allocations,
      { allocationAmount: null, allocationRate: 0, driverId: "" },
    ]);
  };

  const allocationTypes = [
    { id: "$", label: "$" },
    { id: "$", label: "%" },
  ];

  const getAllocationType = (index: number) => {
    if (allocations[index].allocationAmount !== null) {
      return [{ id: "$", label: "$" }];
    }
    if (allocations[index].allocationRate !== null) {
      return [{ id: "%", label: "%" }];
    }
  };

  const handleChangeAllocationType = (index: number) => {
    if (allocations[index].allocationAmount !== null) {
      const updatedAllocations = [...allocations];
      allocations[index].allocationAmount = null;
      allocations[index].allocationRate = 0;
      updateAllocations(updatedAllocations);
      return;
    }
    if (allocations[index].allocationRate !== null) {
      const updatedAllocations = [...allocations];
      allocations[index].allocationRate = null;
      allocations[index].allocationAmount = 0;
      updateAllocations(updatedAllocations);
      return;
    }
  };

  return (
    <div className="p-1 bg-white dark:bg-gray-700">
      {errorMessage && (
        <p className="text-red-600 dark:text-red-400 text-sm mb-2 max-w-md">
          {errorMessage}
        </p>
      )}
      {allocations.map((allocation, i) => (
        <div
          className="border dark:border-gray-500 mb-2 p-2 rounded-md"
          key={i}
        >
          <div className="flex items-center gap-2">
            <DriverSelect
              options={driverOptions}
              value={allocation.driverId}
              onChange={(driverId) =>
                driverId && handleDriverChange(i, driverId)
              }
              clearable={false}
              isLoading={getDriversLoading}
              size="mini"
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <div style={{ width: 100 }}>
              <Select
                clearable={false}
                size={SIZE.mini}
                placeholder={""}
                options={allocationTypes}
                value={getAllocationType(i)}
                onChange={() => handleChangeAllocationType(i)}
              />
            </div>
            {allocation.allocationRate !== null && (
              <PercentInput
                size="mini"
                startEnhancer=""
                percent={allocation.allocationRate * 100}
                setPercent={(value) => handleRateChange(i, Number(value))}
                placeholder="Enter allocation rate"
              />
            )}
            {allocation.allocationAmount !== null && (
              <DollarInput
                size="mini"
                startEnhancer=""
                dollar={parseFloat(allocation.allocationAmount)}
                setDollar={(value) => handleAmountChange(i, Number(value))}
                placeholder="Enter allocation amount"
              />
            )}
            <TrashIcon
              style={{ width: 50 }}
              onClick={() => handleRemoveAllocation(i)}
              className=" h-5 w-5 inline-block cursor-pointer hover:bg-slate-200 dark:hover:bg-gray-600 dark:text-gray-400 rounded-full p-0.5"
            />
          </div>
        </div>
      ))}
      <CourieButton
        size={"xs"}
        color={"secondary"}
        onClick={handleAddAllocation}
        className={
          "cursor-pointer text-blue-500 dark:text-blue-400 hover:underline"
        }
      >
        <PlusCircleIcon className="h-5 w-5 mr-2" />
        Add Allocation
      </CourieButton>

      <div className="flex justify-end mt-2 border-t dark:border-gray-500 pt-2">
        <CourieButton size={"xs"} onClick={handleSave} isProcessing={loading}>
          Save Changes
        </CourieButton>
      </div>
    </div>
  );
}

export default ReplaceAllocationView;
